import { useEffect, useRef, useState } from "react";
import { useQuery, useInfiniteQuery, useIsMutating, useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { useToast } from "@/hooks/use-toast";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { getFreshSession } from "@/lib/auth-utils";
import { useCurrentUserRole } from "@/hooks/use-workspace-members";
import { formatMoney } from "@/lib/money";
import {
  type AutoReloadSettingsInput,
  type BillingError,
  type CheckoutStatus,
  type SubscribeAnswer,
  type WorkspaceBilling,
  type WorkspaceBillingTransaction,
  confirmSubscription,
  createCheckout,
  getAutoReloadFailure,
  getCheckoutStatus,
  getSpend,
  getWallet,
  listPayments,
  portalLink,
  removePaymentMethod,
  saveAutoReload,
  setUpMandate,
  startSubscription,
  topUp,
} from "@/services/billing.service";

export type { AutoReloadSettingsInput, BillingError, CheckoutStatus, WorkspaceBilling, WorkspaceBillingTransaction };

/** Reload cap Stripe registers the e-mandate with when the row has none yet. */
export const INDIA_AUTO_RELOAD_CAP = 150;

/**
 * Owner or admin: the only roles every billing write accepts (the rest get a
 * 403 from the server), so only they see the buttons. Undefined while the role
 * is loading.
 */
export const useCanManageBilling = (): boolean | undefined => {
  const { currentWorkspaceId } = useWorkspace();
  const { data: role, isLoading } = useCurrentUserRole(currentWorkspaceId);
  if (isLoading) return undefined;
  return role === "owner" || role === "admin";
};

export const isIndiaCard = (billing: Pick<WorkspaceBilling, "payment_method_country"> | null | undefined) =>
  billing?.payment_method_country === "IN";

/**
 * The time Stripe starts a bank-held reload (completes_at): local time, plus IST
 * when the browser is elsewhere and the card is Indian (the bank's notice quotes
 * IST). Stripe confirms the capture minutes after this, so copy says "after".
 */
export const formatLandsBy = (iso: string | null | undefined, withIst = false): string => {
  if (!iso) return "about a day after the request";
  const date = new Date(iso);
  const options: Intl.DateTimeFormatOptions = {
    weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit",
  };
  const local = date.toLocaleString(undefined, options);
  if (!withIst || Intl.DateTimeFormat().resolvedOptions().timeZone === "Asia/Kolkata") return local;
  return `${local} (IST: ${date.toLocaleString(undefined, { ...options, timeZone: "Asia/Kolkata" })})`;
};

/** The workspace's money, from the `billing` function. */
export const useWorkspaceBilling = () => {
  const { currentWorkspaceId } = useWorkspace();

  return useQuery({
    queryKey: ["workspace-billing", currentWorkspaceId],
    // A manual refetch runs this even with the query switched off.
    queryFn: () => {
      if (!currentWorkspaceId) throw new Error("No workspace selected");
      return getWallet(currentWorkspaceId);
    },
    enabled: !!currentWorkspaceId,
    // While a bank-held reload is pending, poll so the banner and balance update
    // on their own once Stripe confirms the capture.
    refetchInterval: (query) => (query.state.data?.auto_reload_pending ? 60_000 : false),
  });
};

// The transaction types Payment History shows. The server answers only these:
// call usage rows vastly outnumber them, so they are left out in the query.
export const PAYMENT_TRANSACTION_TYPES: WorkspaceBillingTransaction['transaction_type'][] = [
  'top_up',
  'auto_reload',
  'refund',
  'adjustment',
  'subscription_charge',
  'top_up_failed',
  'subscription_refund',
];

export const TRANSACTIONS_PAGE_SIZE = 20;

interface TransactionPage {
  items: WorkspaceBillingTransaction[];
  total: number;
}

// Paginated: a workspace on auto-reload accumulates payment rows indefinitely,
// so the list loads a page at a time rather than capping at an arbitrary number.
export const useWorkspaceBillingTransactions = (pageSize = TRANSACTIONS_PAGE_SIZE) => {
  const { currentWorkspaceId } = useWorkspace();

  return useInfiniteQuery({
    queryKey: ["workspace-billing-transactions", currentWorkspaceId, pageSize],
    initialPageParam: 1,
    // A failed page throws rather than answering []: an empty page ends
    // pagination, so swallowing it would silently truncate the history.
    queryFn: async ({ pageParam }): Promise<TransactionPage> => {
      const { rows, total } = await listPayments(currentWorkspaceId as string, pageParam, pageSize);
      return { items: rows, total };
    },
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
      return loaded < lastPage.total ? allPages.length + 1 : undefined;
    },
    enabled: !!currentWorkspaceId,
  });
};

/** What a threshold hint is before the server has answered, or with nothing to go on. */
const NO_SPEND = { hasData: false, dailySpend: 0, suggestedThreshold: 20 };

/**
 * Threshold hint for bank-held reloads: funds land ~a day after the trigger, so
 * the threshold should cover more than a day of spend. The server works it out
 * from the trailing week (×1.5, between $20 and the highest trigger a save takes).
 */
export const useDailySpendSuggestion = (enabled = true) => {
  const { currentWorkspaceId } = useWorkspace();
  const { data } = useQuery({
    queryKey: ["workspace-spend", currentWorkspaceId],
    queryFn: () => getSpend(currentWorkspaceId as string),
    enabled: enabled && !!currentWorkspaceId,
  });
  if (!data) return NO_SPEND;
  return { hasData: data.has_data, dailySpend: data.daily_spend, suggestedThreshold: data.suggested_threshold };
};

/** Both wallet-facing queries always refresh together. */
const invalidateBilling = (queryClient: QueryClient, workspaceId: string | null) => {
  queryClient.invalidateQueries({ queryKey: ["workspace-billing", workspaceId] });
  queryClient.invalidateQueries({ queryKey: ["workspace-billing-transactions", workspaceId] });
};

/** The plan and the row whose call limit it sets always refresh together. */
const invalidatePlan = (queryClient: QueryClient, workspaceId: string | null) => {
  queryClient.invalidateQueries({ queryKey: ["workspace-billing", workspaceId] });
  queryClient.invalidateQueries({ queryKey: ["workspace-subscription", workspaceId] });
};

/**
 * Keep refreshing for 30 s while Stripe's webhook writes what just happened: a
 * credit landing in the wallet, or a paid upgrade switching the plan on. The
 * webhook, not the response we just got, is what actually changes them.
 */
const keepRefreshing = (refresh: () => void) => {
  const deadline = Date.now() + 30_000;
  const timer = setInterval(() => {
    refresh();
    if (Date.now() >= deadline) clearInterval(timer);
  }, 3_000);
};

export interface TopUpVariables {
  amount: number;
  /**
   * Idempotency key for one top-up *attempt*. Generated when the dialog opens and
   * reused for retries of that attempt, so a double click or a retried request
   * cannot charge the card twice.
   */
  requestId: string;
  /** Force Stripe Checkout even when a card is saved (used after REQUIRES_ACTION). */
  viaCheckout?: boolean;
}

export const useTopUpWorkspaceWallet = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { currentWorkspaceId } = useWorkspace();

  return useMutation({
    mutationFn: async ({ amount, requestId, viaCheckout }: TopUpVariables) => {
      const session = await getFreshSession();
      if (!session) {
        throw new Error("Your session has expired. Please log in again.");
      }
      if (!currentWorkspaceId) throw new Error("No workspace selected");

      const cached = queryClient.getQueryData<WorkspaceBilling | null>([
        "workspace-billing",
        currentWorkspaceId,
      ]);
      // Cards issued in India are always charged customer-present through
      // Checkout (RBI rules); the saved-card path would only answer USE_CHECKOUT.
      const useSavedCard = !viaCheckout && !isIndiaCard(cached) && !!cached?.payment_method_added;

      if (!useSavedCard) {
        const { checkout_url } = await createCheckout(currentWorkspaceId, amount);
        return { mode: "checkout" as const, checkoutUrl: checkout_url };
      }

      // The code is carried so the dialog can offer secure checkout on
      // REQUIRES_ACTION / USE_CHECKOUT. Only this charge can have taken money
      // unseen; a Checkout session charges nothing.
      const data = await topUp(currentWorkspaceId, amount, requestId).catch((error: BillingError) => {
        throw Object.assign(error, { charge: true });
      });

      // The card was charged and the wallet credit is following via the
      // webhook. That is not a failure — never surface it as one.
      return { mode: data.status === "pending" ? ("pending" as const) : ("direct" as const), balanceAfter: data.balance_after };
    },
    onSuccess: (result) => {
      if (result.mode === "checkout") {
        window.location.href = result.checkoutUrl;
        return;
      }

      if (result.mode === "pending") {
        keepRefreshing(() => invalidateBilling(queryClient, currentWorkspaceId));
        toast({
          title: "Payment received",
          description: "Your wallet will update in a few seconds.",
        });
        return;
      }

      // A retry that found its own charge already credited is still this top-up completing (D1-V1).
      invalidateBilling(queryClient, currentWorkspaceId);
      toast({
        title: "Top-up completed",
        description:
          result.balanceAfter !== null
            ? `New wallet balance: ${formatMoney(result.balanceAfter)}`
            : "Your wallet has been topped up.",
      });
    },
    onError: (error: BillingError) => {
      // These get their own in-dialog prompt to retry via secure checkout.
      if (isCheckoutRetryCode(error.code)) return;
      toast({
        variant: "destructive",
        title: "Failed to top up",
        description: error.message,
      });
    },
  });
};

/** Top-up failures the dialog resolves by retrying the same attempt through Checkout. */
export const isCheckoutRetryCode = (code: string | null | undefined) =>
  code === "REQUIRES_ACTION" || code === "USE_CHECKOUT";

/**
 * Contract C6: a top-up whose outcome is unknown. The saved-card charge got no
 * answer, or the server answered 5xx (503 RETRY_SAME_REQUEST among them): the
 * card may have been charged. The retry must reuse the same request id and
 * amount, and Stripe answers it with the charge it already made. Every other
 * answer is final, and so is any failure to open Checkout, which charges
 * nothing: the next attempt gets a new id.
 */
export const isTopUpUnconfirmed = (error: unknown) => {
  const failure = error as BillingError | null;
  const status = failure?.status;
  return !!failure?.charge && (status === 0 || status >= 500);
};

const CHECKOUT_POLL_MS = 3_000;
const CHECKOUT_TIMEOUT_MS = 60_000;

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Confirms a return from Stripe Checkout. `?checkout=success` only means Stripe
 * redirected us back — the wallet is credited by the Stripe webhook, so the only
 * honest signal is `credited` from the read-only status endpoint. Poll for it and
 * never claim a success we haven't seen.
 *
 * Returns whether a confirmation is still in flight, for a non-blocking banner.
 */
export const useCheckoutStatus = (sessionId: string | null) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { currentWorkspaceId } = useWorkspace();
  const [isConfirming, setIsConfirming] = useState(false);
  // StrictMode mounts effects twice; poll each session id exactly once. The loop
  // is deliberately not cancelled on unmount (it is bounded, and the toast store
  // is global) so the dev-mode remount does not leave the confirmation dead.
  const polledSessionId = useRef<string | null>(null);

  useEffect(() => {
    // The workspace is the one Stripe sent the customer back to, once it is known.
    if (!sessionId || !currentWorkspaceId || polledSessionId.current === sessionId) return;
    polledSessionId.current = sessionId;

    setIsConfirming(true);

    const settle = (message: Parameters<typeof toast>[0]) => {
      invalidateBilling(queryClient, currentWorkspaceId);
      setIsConfirming(false);
      toast(message);
    };

    void (async () => {
      const deadline = Date.now() + CHECKOUT_TIMEOUT_MS;
      // The last status the endpoint actually returned. A failed poll proves nothing either way.
      let last: CheckoutStatus | null = null;

      while (Date.now() < deadline) {
        last = (await getCheckoutStatus(currentWorkspaceId, sessionId).catch(() => null)) ?? last;

        if (last?.credited) {
          return settle({
            title: "Payment successful",
            description: `${formatMoney(last.amount)} added to your wallet.`,
          });
        }
        if (last?.payment_status === "unpaid") {
          return settle({
            variant: "destructive",
            title: "Payment not completed",
            description: "Stripe reported this payment as unpaid, so nothing was added to your wallet.",
          });
        }
        if (last?.payment_status === "no_payment_required") {
          return settle({
            title: "No payment taken",
            description: "This checkout didn't need a payment, so your wallet balance is unchanged.",
          });
        }

        await sleep(CHECKOUT_POLL_MS);
      }

      // Out of time. Only a status that said "paid" makes this a late credit; anything
      // else (every poll failing included) is an unconfirmed payment, and says so.
      settle(
        last?.payment_status === "paid"
          ? {
              title: "Payment received",
              description: "It's taking a little longer than usual to appear; it will show up automatically.",
            }
          : {
              title: "Payment not confirmed yet",
              description: "We couldn't confirm this payment yet — it will appear in Payment History if it went through.",
            },
      );
    })();
  }, [sessionId, currentWorkspaceId, queryClient, toast]);

  return { isConfirming };
};

export interface AutoReloadFailure {
  reason: string;
  occurredAt: string;
  /** True when this failure turned auto-reload off (vs. a strike that is still counting). */
  disabled: boolean;
  declineCode: string | null;
  /** Consecutive failures so far ("attempt N of 3"). */
  attempt: number | null;
}

export const useLatestAutoReloadFailure = () => {
  const { currentWorkspaceId } = useWorkspace();

  return useQuery({
    queryKey: ["workspace-auto-reload-failure", currentWorkspaceId],
    // Strikes 1 and 2 count too, not only the disabling third one; the server
    // answers null once a reload has gone through since. A failed read shows no
    // failure: the banner is news, not a reason to show an error.
    queryFn: async (): Promise<AutoReloadFailure | null> => {
      const failure = await getAutoReloadFailure(currentWorkspaceId as string).catch(() => null);
      return failure && {
        reason: failure.reason,
        occurredAt: failure.occurred_at,
        disabled: failure.disabled,
        declineCode: failure.decline_code,
        attempt: failure.attempt,
      };
    },
    enabled: !!currentWorkspaceId,
  });
};

export const useUpdateWorkspaceAutoReload = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { currentWorkspaceId } = useWorkspace();

  return useMutation({
    mutationFn: async (settings: AutoReloadSettingsInput) => {
      if (!currentWorkspaceId) throw new Error("No workspace selected");

      const session = await getFreshSession();
      if (!session) {
        throw new Error("Your session has expired. Please log in again.");
      }

      return saveAutoReload(currentWorkspaceId, settings);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspace-billing", currentWorkspaceId] });
      queryClient.invalidateQueries({ queryKey: ["workspace-auto-reload-failure", currentWorkspaceId] });
      toast({
        title: "Settings updated",
        description: "Auto-reload settings have been updated.",
      });
    },
    onError: (error: Error & { code?: string }) => {
      // The component answers MANDATE_REQUIRED by running the bank verification.
      if (error.code === "MANDATE_REQUIRED") return;
      toast({
        variant: "destructive",
        title: "Failed to update settings",
        description: error.message,
      });
    },
  });
};

const STRIPE_LOAD_FAILED =
  "Couldn't load Stripe's verification. Check your connection or ad-blocker and try again.";

// Stripe.js is loaded once per publishable key; a failed load is forgotten so a
// retry (after the customer unblocks it) can try again.
const stripeByKey = new Map<string, Promise<Stripe | null>>();
const getStripe = (publishableKey: string) => {
  let promise = stripeByKey.get(publishableKey);
  if (!promise) {
    promise = loadStripe(publishableKey).catch((err) => {
      stripeByKey.delete(publishableKey);
      throw err;
    });
    stripeByKey.set(publishableKey, promise);
  }
  return promise;
};

export type MandatePhase = "saving" | "verifying" | "finalising" | null;

export const PHASE_LABEL: Record<NonNullable<MandatePhase>, string> = {
  saving: "Saving…",
  verifying: "Verifying with your bank…",
  finalising: "Finishing up…",
};

/**
 * Turn auto-reload on. For a card issued in India the server answers
 * MANDATE_REQUIRED until the bank has authorised off-session charges; this
 * runs that authorisation as one mutation — setup → bank's 3DS/OTP (Stripe.js
 * modal) → finalise → enable — so the switch never shows "on" early.
 * Non-India cards take the first call and return.
 */
export const useEnableAutoReloadWithMandate = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { currentWorkspaceId } = useWorkspace();
  const [phase, setPhase] = useState<MandatePhase>(null);

  const mutation = useMutation({
    mutationFn: async (settings: Omit<AutoReloadSettingsInput, "auto_reload_enabled">) => {
      if (!currentWorkspaceId) throw new Error("No workspace selected");

      const session = await getFreshSession();
      if (!session) {
        throw new Error("Your session has expired. Please log in again.");
      }

      const enable = { ...settings, auto_reload_enabled: true };

      setPhase("saving");
      try {
        return await saveAutoReload(currentWorkspaceId, enable);
      } catch (error) {
        if ((error as { code?: string }).code !== "MANDATE_REQUIRED") throw error;
      }

      setPhase("verifying");
      const setup = await setUpMandate(currentWorkspaceId);

      if (setup.required) {
        if (!setup.publishable_key || !setup.client_secret || !setup.payment_method_id || !setup.setup_intent_id) {
          throw new Error("The bank authorisation could not be started. Please refresh and try again.");
        }

        const stripe = await getStripe(setup.publishable_key).catch(() => null);
        if (!stripe) throw new Error(STRIPE_LOAD_FAILED);

        // Stripe shows the bank's 3DS/OTP challenge in its own modal.
        const { setupIntent, error } = await stripe.confirmCardSetup(setup.client_secret, {
          payment_method: setup.payment_method_id,
        });
        if (error) throw new Error(error.message || "Your bank did not complete the authorisation.");
        if (setupIntent?.status !== "succeeded") {
          throw new Error("Your bank did not complete the authorisation. Please try again.");
        }

        setPhase("finalising");
        await setUpMandate(currentWorkspaceId, setup.setup_intent_id);
      }

      setPhase("finalising");
      return saveAutoReload(currentWorkspaceId, enable);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspace-billing", currentWorkspaceId] });
      queryClient.invalidateQueries({ queryKey: ["workspace-auto-reload-failure", currentWorkspaceId] });
      toast({
        title: "Settings updated",
        description: "Auto-reload settings have been updated.",
      });
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Couldn't turn on auto-reload",
        description: error.message,
      });
    },
    onSettled: () => setPhase(null),
  });

  return { ...mutation, phase };
};

export type SubscribePhase = "starting" | "verifying" | "finalising" | null;

/**
 * After the bank approved the payment, a finish that got no answer, or that the
 * server failed to save, is still a payment received: Stripe's webhook switches
 * Pro on without it. A second subscription that could not be cancelled or
 * refunded is support's to clean up, and a refusal is an answer.
 */
const paidAnyway = (error: BillingError) =>
  error.status === 0 || (Number(error.status) >= 500 && !String(error.code).startsWith("DUPLICATE_"));

export const SUBSCRIBE_PHASE_LABEL: Record<NonNullable<SubscribePhase>, string> = {
  starting: "Processing…",
  // The bank shows its own prompt when it wants one; most cards need none.
  verifying: "Confirming payment…",
  finalising: PHASE_LABEL.finalising,
};

/**
 * Upgrade to Pro on the saved card: start → the first payment confirmed with the
 * bank (Stripe.js; the bank's 3DS/OTP modal when it asks, and always for a card
 * issued in India, whose renewals need the authorisation it registers) → finish.
 *
 * Contract C7. `requestId` is one subscribe attempt: the caller keeps it while
 * retrying that attempt, so a double click or a retried request is one
 * subscription in Stripe. A new attempt resumes an unconfirmed one server-side.
 */
export const useSubscribePro = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { currentWorkspaceId } = useWorkspace();
  const [phase, setPhase] = useState<SubscribePhase>(null);
  const mutationKey = ["subscribe-pro", currentWorkspaceId];
  // An upgrade outlives the card that started it: a remount must not offer a second one.
  const running = useIsMutating({ mutationKey }) > 0;

  const mutation = useMutation({
    mutationKey,
    mutationFn: async ({ requestId }: { requestId: string }) => {
      setPhase("starting");
      const session = await getFreshSession();
      if (!session) {
        throw new Error("Your session has expired. Please log in again.");
      }
      if (!currentWorkspaceId) throw new Error("No workspace selected");

      // 409 ALREADY_SUBSCRIBED (e.g. an existing past_due subscription) arrives as the error.
      const start = await startSubscription(currentWorkspaceId, requestId);
      if (start.status !== "requires_confirmation") return start;
      if (!start.publishable_key || !start.client_secret || !start.payment_method_id || !start.subscription_id) {
        throw new Error("The payment could not be started. Please refresh and try again.");
      }

      setPhase("verifying");
      const stripe = await getStripe(start.publishable_key).catch(() => null);
      if (!stripe) throw new Error(STRIPE_LOAD_FAILED);

      // The saved card pays; Stripe shows the bank's challenge in its own modal if there is one.
      // A payment another tab confirmed a moment ago is refused, with the payment as it stands.
      const { paymentIntent, error } = await stripe.confirmCardPayment(start.client_secret, {
        payment_method: start.payment_method_id,
      });
      const status = (paymentIntent ?? error.payment_intent)?.status;
      if (status !== "succeeded" && status !== "processing") {
        throw new Error(
          error ? error.message || "Your bank did not approve the payment." : "Your bank did not approve the payment. Please try again.",
        );
      }

      setPhase("finalising");
      return confirmSubscription(currentWorkspaceId, start.subscription_id).catch((error: BillingError) => {
        if (paidAnyway(error)) return { status: "processing" } satisfies SubscribeAnswer;
        throw error;
      });
    },
    onSuccess: (result) => {
      if (result.status === "processing") {
        keepRefreshing(() => invalidatePlan(queryClient, currentWorkspaceId));
        toast({
          title: "Payment received",
          description: "Pro switches on in a moment.",
        });
        return;
      }
      toast({
        title: "Subscription created",
        description: "Pro subscription has been activated.",
      });
    },
    onError: (error: BillingError) => {
      toast({
        variant: "destructive",
        title: "Failed to subscribe",
        description:
          error.code === "DUPLICATE_NOT_CANCELLED"
            ? `${error.message} Please contact support with this message so the extra subscription is cancelled.`
            : error.message,
      });
    },
    // Refetch either way: on ALREADY_SUBSCRIBED the server knows about a
    // subscription this client does not.
    onSettled: () => {
      setPhase(null);
      invalidatePlan(queryClient, currentWorkspaceId);
    },
  });

  return { ...mutation, phase, running };
};

export const useCreatePortalSession = () => {
  const { toast } = useToast();
  const { currentWorkspaceId } = useWorkspace();

  return useMutation({
    mutationFn: async () => {
      const session = await getFreshSession();
      if (!session) {
        throw new Error("Your session has expired. Please log in again.");
      }
      if (!currentWorkspaceId) throw new Error("No workspace selected");

      return portalLink(currentWorkspaceId);
    },
    onSuccess: ({ url }) => {
      window.location.href = url;
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Failed to open subscription portal",
        description: error.message,
      });
    },
  });
};

export const useRemovePaymentMethod = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { currentWorkspaceId } = useWorkspace();

  return useMutation({
    // A refusal covers 409 SUBSCRIPTION_ACTIVE ("Cancel your Pro subscription first").
    mutationFn: async ({ workspace_id }: { workspace_id: string }) => {
      const session = await getFreshSession();
      if (!session) {
        throw new Error("Your session has expired. Please log in again.");
      }

      return removePaymentMethod(workspace_id);
    },
    onSuccess: ({ pending_reload: held }) => {
      queryClient.invalidateQueries({ queryKey: ["workspace-billing", currentWorkspaceId] });
      toast({
        title: "Payment method removed",
        description: "Your payment method has been removed successfully.",
      });
      // Only bank-held (India e-mandate) reloads can be pending, so IST applies.
      if (held) {
        toast({
          title: "A reload is still on its way",
          description: `A ${formatMoney(held.amount)} reload already announced to your bank will still be completed after ${formatLandsBy(held.completes_at, true)}.`,
        });
      }
    },
    onError: (error: Error) => {
      toast({
        variant: "destructive",
        title: "Failed to remove payment method",
        description: error.message,
      });
    },
  });
};
